import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import rushLogo from './rush-logo.png';
import './rush.css';
import './auth.css';
import { SSOIcons, AuthMarketingPanel } from './AuthShared';

const VALID_PLANS = ['free', 'essential', 'growth', 'scale'];

function scorePassword(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 8) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return s;
}

const HELPERS = {
  0: '8+ characters with mixed case, a number, and a symbol.',
  1: 'Weak — add mixed case, numbers, or a symbol.',
  2: 'Okay — a symbol would make it strong.',
  3: 'Strong.',
  4: 'Excellent.',
};

const RushSignUpPage = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [optInUpdates, setOptInUpdates] = useState(true);

  const [accountType, setAccountType] = useState('free');
  const [teamId, setTeamId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const score = scorePassword(pwd);

  useEffect(() => {
    const urlPlan = searchParams.get('plan');
    if (urlPlan && VALID_PLANS.includes(urlPlan)) setAccountType(urlPlan);
    // Capture teamId from hash-based router query (e.g. /#/register?teamId=abc)
    const queryParams = window.location.hash.split('?')[1];
    if (queryParams) {
      const t = new URLSearchParams(queryParams).get('teamId');
      if (t) setTeamId(t);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Please enter your full name.'); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setError('Please enter a valid email address.'); return; }
    if (pwd.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!agreeTerms) { setError('Please accept the Terms and Privacy Policy to continue.'); return; }
    setSubmitting(true);
    try {
      await axios.post('/api/users/register', {
        username: name.trim(),
        email,
        password: pwd,
        teamId: teamId || null,
        accountType,
      });
      setSuccess('Registration successful! Check your email and click the verification link to activate your account.');
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rs auth-page">
      <section className="auth-form-panel">
        <header className="auth-form-header">
          <Link to="/" className="rs-logo rs-logo-img" aria-label="Rush Studio home">
            <img src={rushLogo} alt="Rush Studio" />
          </Link>
          <div className="auth-form-header-link">
            Already have an account?<Link to="/login">Sign in</Link>
          </div>
        </header>

        <div className="auth-form-body">
          <div className="auth-eyebrow">Create your workspace</div>
          <h1 className="auth-title">Start translating today.</h1>
          <p className="auth-sub">14-day free trial · no credit card · cancel any time. Your workspace is ready in under a minute.</p>

          {success ? (
            <>
              <div className="magic-success" role="status">
                <div className="magic-success-icon">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 4h12v10H3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M3 4l6 5 6-5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
                </div>
                <div className="magic-success-text">
                  <b>Check your inbox.</b>
                  {success}
                  <div style={{ marginTop: 10 }}>
                    <Link className="btn btn-ghost btn-sm" to="/login">Go to sign in</Link>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="sso-row">
                <button type="button" className="sso-btn" disabled>{SSOIcons.google}Google</button>
                <button type="button" className="sso-btn" disabled>{SSOIcons.microsoft}Microsoft</button>
                <button type="button" className="sso-btn" disabled>{SSOIcons.github}GitHub</button>
              </div>

              <div className="auth-divider">or with email</div>

              {error && (
                <div style={{ padding: '10px 14px', background: 'var(--accent-soft)', color: 'var(--accent-2)', borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 14 }} role="alert">
                  {error}
                </div>
              )}

              <form className="auth-form" onSubmit={handleSubmit}>
                <div className="field-row">
                  <label className="field">
                    <span>Full name</span>
                    <input className="input input-lg" placeholder="Sara Kowalski" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Work email</span>
                    <input type="email" className="input input-lg" placeholder="sara@yourcompany.com" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </label>
                </div>

                <div className="field-row">
                  <label className="field">
                    <span>Company</span>
                    <input className="input input-lg" placeholder="Acme Inc." autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Team size</span>
                    <select className="input input-lg" value={teamSize} onChange={(e) => setTeamSize(e.target.value)}>
                      <option value="" disabled>Choose…</option>
                      <option>Just me</option>
                      <option>2–10</option>
                      <option>11–50</option>
                      <option>51–200</option>
                      <option>200+</option>
                    </select>
                  </label>
                </div>

                <label className="field">
                  <span>Password</span>
                  <div className="input-with-action">
                    <input type={showPwd ? 'text' : 'password'} className="input input-lg" placeholder="At least 8 characters" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={8} autoComplete="new-password" />
                    <button type="button" className="field-action" onClick={() => setShowPwd((s) => !s)}>{showPwd ? 'Hide' : 'Show'}</button>
                  </div>
                  <div className={`password-meter s-${score}`}>
                    <span></span><span></span><span></span><span></span>
                  </div>
                  <div className="field-helper">{HELPERS[score]}</div>
                </label>

                <label className="checkbox-row">
                  <input type="checkbox" required checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} />
                  <span>I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.</span>
                </label>

                <label className="checkbox-row">
                  <input type="checkbox" checked={optInUpdates} onChange={(e) => setOptInUpdates(e.target.checked)} />
                  <span>Send me product updates &amp; localization tips. <span style={{ color: 'var(--ink-4)' }}>(No spam, monthly at most.)</span></span>
                </label>

                <button className="btn btn-accent auth-submit" type="submit" disabled={submitting}>
                  {submitting ? 'Creating workspace…' : 'Create my workspace →'}
                </button>
              </form>
            </>
          )}

          <div className="auth-aside-link">Looking for enterprise pricing? <Link to="/contact">Talk to sales →</Link></div>
        </div>

        <footer className="auth-form-footer">
          <span>© {new Date().getFullYear()} Rush Studio Ltd.</span>
          <span><Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link> · <Link to="/contact">Help</Link></span>
        </footer>
      </section>

      <AuthMarketingPanel variant="features" />
    </div>
  );
};

export default RushSignUpPage;
